import type {
  ArtifactListingEntry,
  ConversationCard,
  WidgetDataRequest,
} from '@crowdex/agent-contracts/api';
import { apiFetch } from '@crowdex/host-kit/renderer/http';
import {
  WIDGET_DATA_KINDS,
  type WidgetDataKind,
  widgetResultSchema,
} from '@crowdex/plugin-contracts';
import {
  Artifact,
  ArtifactContent,
  ArtifactDescription,
  ArtifactHeader,
  ArtifactTitle,
} from '@crowdex/ui/ai/artifact';
import { Button } from '@crowdex/ui/components/button';
import { Popover, PopoverContent, PopoverTrigger } from '@crowdex/ui/components/popover';
import { WidgetContent, type WidgetState } from '@crowdex/ui/widgets';
import {
  Check,
  CircleDashed,
  CircleSlash,
  Ellipsis,
  ExternalLink,
  Hourglass,
  LoaderCircle,
  X,
} from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { TRANSCRIPT_CARD } from '@/components/desktop-message-part/command-card';
import { type AppAction, railPanels } from '@/features/plugins/plugin-workbench';
import { useWorkbench } from '@/features/plugins/use-plugin-workbench';
import { useDesktopPreviewState } from '@/lib/desktop-preview-state';
import { useDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import { askAgent, attachToAgent } from './agent-ask';
import { WidgetCardActions } from './app-actions';
import { AppFrame } from './app-frame';
import { ArtifactPreview } from './artifact-preview';
import { ArtifactView, WhenVisible } from './artifact-view';
import { CommonViewer } from './common-viewers/common-viewer';
import { isCoreOpenTarget, useOpenCoreTarget } from './core-open-targets';
import { dockArtifactTarget } from './dock-artifact-target';
import { openAppRoute, useOpenAppRoute } from './open-app-route';
import { PluginFolderActions } from './plugin-folder';
import { latestRender } from './renders';
import { artifactOfFile } from './use-artifact-index';

/** How often cards still in progress (running, waiting) are read again while shown. */
const POLL_MS = 5_000;

const NO_CARDS: readonly ConversationCard[] = [];

function listConversationCards(conversation: string): Promise<ConversationCard[]> {
  return apiFetch(`/api/conversation-cards?conversation=${encodeURIComponent(conversation)}`, {
    cache: 'no-store',
  });
}

/**
 * The cards apps put in a conversation: read when it opens, when `refreshKey` changes (a message
 * came or a turn ended) and, while one of them is still in progress, every few seconds.
 */
export function useConversationCards(
  conversation: string | null,
  refreshKey?: unknown,
): readonly ConversationCard[] {
  const [cards, setCards] = useState<readonly ConversationCard[]>(NO_CARDS);
  // Another conversation shows none of the previous one's cards.
  const [cardsOf, setCardsOf] = useState(conversation);
  if (cardsOf !== conversation) {
    setCardsOf(conversation);
    setCards(NO_CARDS);
  }
  const latest = useRef(conversation);
  latest.current = conversation;
  const refresh = useCallback(() => {
    if (conversation === null) return;
    void listConversationCards(conversation).then(
      (next) => {
        if (latest.current !== conversation) return;
        setCards((current) => (JSON.stringify(current) === JSON.stringify(next) ? current : next));
      },
      () => undefined,
    );
  }, [conversation]);
  // biome-ignore lint/correctness/useExhaustiveDependencies(refreshKey): the caller's signal to read the cards again (a message came, a turn ended).
  useEffect(() => {
    refresh();
  }, [refresh, refreshKey]);
  const live = cards.some((card) => card.status === 'running' || card.status === 'waiting');
  useEffect(() => {
    if (!live) return;
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [live, refresh]);
  // The redesign's preview shows its boards' cards (no server answers it).
  const previewCards = useDesktopPreviewState((state) => state.cards);
  return previewCards ?? cards;
}

const STATUS = {
  info: { Icon: CircleDashed, label: '', tone: 'text-muted-foreground' },
  running: { Icon: LoaderCircle, label: 'In progress', tone: 'text-muted-foreground' },
  waiting: { Icon: Hourglass, label: 'Waits on you', tone: 'text-attention' },
  succeeded: { Icon: Check, label: 'Done', tone: 'text-success' },
  failed: { Icon: X, label: 'Failed', tone: 'text-destructive' },
  skipped: { Icon: CircleSlash, label: 'Skipped', tone: 'text-muted-foreground' },
} as const;

/** A placed widget's data, read when it is shown and again when its card changes. */
function useWidgetData(
  ref: { widget: string; params: Record<string, unknown> },
  kind: string,
  conversation: string | undefined,
  revision: unknown,
): WidgetState {
  const [state, setState] = useState<WidgetState>({ status: 'loading' });
  const key = JSON.stringify([ref.widget, ref.params, kind, conversation]);
  useEffect(() => {
    void revision;
    let live = true;
    const [widget, params, dataKind, inConversation] = JSON.parse(key) as [
      string,
      Record<string, unknown>,
      string,
      string | undefined,
    ];
    apiFetch<unknown>('/api/widgets/data', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        widget,
        params,
        ...(isDataKind(dataKind) ? { kind: dataKind } : {}),
        ...(inConversation === undefined ? {} : { conversation: inConversation }),
      } satisfies WidgetDataRequest),
    })
      .then((answer) => widgetResultSchema.parse(answer))
      .then(
        (result) => {
          if (!live) return;
          setState(
            result.ok
              ? { status: 'ready', data: result.data, fetchedAt: result.fetchedAt }
              : 'waiting' in result
                ? { status: 'waiting', ...result.waiting }
                : { status: 'failed', error: result.error },
          );
        },
        (error: unknown) =>
          live &&
          setState({
            status: 'failed',
            error: error instanceof Error ? error.message : String(error),
          }),
      );
    return () => {
      live = false;
    };
  }, [key, revision]);
  const preview = useDesktopPreviewState((current) => current.widgetData[ref.widget]);
  return preview ?? state;
}

const isDataKind = (kind: string): kind is WidgetDataKind =>
  (WIDGET_DATA_KINDS as readonly string[]).includes(kind);

/** The artifact a card shows, from the conversation's artifact index. */
function artifactOfCard(
  card: ConversationCard,
  artifacts: readonly ArtifactListingEntry[],
): ArtifactListingEntry | undefined {
  const target = card.display.artifact;
  if (target === undefined) return undefined;
  return (
    artifacts.find((entry) => entry.space === target.space && entry.path === target.path) ??
    (target.space === 'workspace' ? artifactOfFile(artifacts, target.path) : undefined)
  );
}

/** A card of the artifact a message created or changed: the core's `crowdex/artifact` widget. */
export function artifactCard(artifact: ArtifactListingEntry): ConversationCard {
  return {
    plugin: 'crowdex',
    id: `artifact:${artifact.id}`,
    afterMessageId: null,
    title: artifact.title,
    summary: null,
    status: 'info',
    widget: {
      widget: 'crowdex/artifact',
      params: { space: artifact.space, path: artifact.path },
    },
    display: {
      kind: 'artifact',
      view: null,
      artifact: { space: artifact.space, path: artifact.path },
    },
    createdAt: artifact.modifiedAt,
    updatedAt: artifact.modifiedAt,
  };
}

/** A data widget, drawn by the core. */
function DataBody({
  card,
  kind,
  conversation,
}: {
  card: ConversationCard;
  kind: WidgetDataKind;
  conversation: string;
}) {
  const state = useWidgetData(card.widget, kind, conversation, card.updatedAt);
  const range =
    typeof card.widget.params['range'] === 'string' ? card.widget.params['range'] : undefined;
  return <WidgetContent kind={kind} presentation={card.display} range={range} state={state} />;
}

/** A view widget: its plugin's view, framed with its params as input. */
function ViewBody({
  card,
  view,
  conversation,
}: {
  card: ConversationCard;
  view: { plugin: string; path: string };
  conversation: string;
}) {
  const workbench = useWorkbench();
  const store = useDesktopShellStoreApi();
  const openCoreTarget = useOpenCoreTarget();
  return (
    <WhenVisible className="h-44 overflow-hidden bg-background">
      <AppFrame
        container="preview"
        plugin={view.plugin}
        handlers={{
          ask: askAgent,
          attach: (selections, send) => attachToAgent(selections, send),
          open: (target) => {
            if (isCoreOpenTarget(target)) {
              openCoreTarget(target);
              return null;
            }
            if ('artifact' in target) {
              void dockArtifactTarget(store, {
                plugin: view.plugin,
                ...target.artifact,
                conversation,
              });
              return null;
            }
            // A route of the app's tab (`/?item=…#/runs/…`) docks in the card's conversation.
            return openAppRoute(store, railPanels(workbench), {
              plugin: view.plugin,
              path: target.path,
              in: target.in,
              input: target.input,
              size: target.size,
              title: card.title,
              conversation,
            });
          },
          closeDialog: () => {
            throw new Error('This view is not a dialog.');
          },
          confirm: ({ title, message }) =>
            window.confirm(message ? `${title}\n\n${message}` : title),
          selectItem: () => undefined,
          refreshPage: () => undefined,
        }}
        input={card.widget.params}
        path={view.path}
        title={card.title}
        workspace={conversation}
      />
    </WhenVisible>
  );
}

/** The widget as this card places it, for the actions that take it (Pin to dashboard). */
function placedRef(card: ConversationCard): Record<string, unknown> {
  const { kind } = card.display;
  return {
    ...card.widget,
    ...(isDataKind(kind) ? { kind } : {}),
    title: card.title,
    ...Object.fromEntries(
      (['format', 'currency', 'better', 'link'] as const).flatMap((key) =>
        card.display[key] === undefined ? [] : [[key, card.display[key]]],
      ),
    ),
  };
}

/** How far a running card is, from its summary ("64 % · about 1 min left" → 0.64). */
export function cardProgress(summary: string | null): number | null {
  const match = summary === null ? null : /^\s*(\d{1,3}(?:[.,]\d+)?)\s?%/.exec(summary);
  if (match?.[1] === undefined) return null;
  const value = Number(match[1].replace(',', '.'));
  return Number.isFinite(value) ? Math.min(1, Math.max(0, value / 100)) : null;
}

/** "updated just now", "updated 8 min ago", "updated 3 h ago", "updated on 2 Oct". */
export function updatedLabel(iso: string, now = Date.now()): string {
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return '';
  const minutes = Math.round((now - at) / 60_000);
  if (minutes < 1) return 'updated just now';
  if (minutes < 60) return `updated ${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `updated ${hours} h ago`;
  return `updated on ${new Date(at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
}

/** A card's other actions (its type's, Pin to dashboard…), behind one quiet "More" button. */
function CardMoreActions({ children }: { children: ReactNode }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          aria-label="More actions"
          className="size-8 text-muted-foreground"
          size="icon-sm"
          variant="ghost"
        >
          <Ellipsis className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-1">
        {children}
      </PopoverContent>
    </Popover>
  );
}

/**
 * One card: a widget under its title and status — data drawn by the core, a plugin's view framed,
 * or an artifact as its type previews it — with its actions under it unless `actions` is false.
 */
export function ConversationAppCard({
  card,
  conversation,
  artifacts = [],
  onOpenArtifact,
  actions = true,
}: {
  card: ConversationCard;
  conversation: string;
  /** The conversation's artifacts, where an artifact card finds its own. */
  artifacts?: readonly ArtifactListingEntry[];
  onOpenArtifact?: ((artifact: ArtifactListingEntry) => void) | undefined;
  actions?: boolean;
}) {
  const workbench = useWorkbench();
  const openRoute = useOpenAppRoute();
  const status = STATUS[card.status];
  const { kind, view } = card.display;
  const artifact = kind === 'artifact' ? artifactOfCard(card, artifacts) : undefined;
  // A source with a render previews it (a video plays on the spot), "Open in panel" its editor.
  const render = artifact === undefined ? null : latestRender(artifact, artifacts);
  const provider = card.widget.widget.slice(0, card.widget.widget.lastIndexOf('/'));
  const source =
    artifact !== undefined
      ? `${artifact.typeTitle} · ${artifact.path}`
      : (workbench.pluginDisplayNames[provider] ??
        (provider === 'crowdex'
          ? 'Crowdex'
          : provider.charAt(0).toUpperCase() + provider.slice(1)));
  const body =
    kind === 'artifact' ? (
      artifact === undefined ? null : (
        <WhenVisible className="pointer-events-none h-56 overflow-hidden bg-background">
          <ArtifactView artifact={artifact} container="preview" />
        </WhenVisible>
      )
    ) : view !== null ? (
      <ViewBody card={card} conversation={conversation} view={view} />
    ) : isDataKind(kind) ? (
      <div className="widget-card px-4 py-3">
        <DataBody card={card} conversation={conversation} kind={kind} />
      </div>
    ) : null;
  const host = {
    conversation,
    // A view widget's own view has its controls: only data and artifacts are pinned.
    ...(view === null ? { widget: { ref: placedRef(card), title: card.title } } : {}),
    ...(artifact === undefined ? {} : { artifact: { title: artifact.title, path: artifact.path } }),
    ask: (text: string) => askAgent(text),
    // A card is the conversation's: an app's route docks in it (or as the action asks: `in`).
    open: (action: AppAction & { run: Extract<AppAction['run'], { open: string }> }) =>
      openRoute({
        plugin: action.plugin,
        path: action.run.open,
        in: action.run.in,
        input: action.run.input,
        size: action.run.size,
        title: action.label,
        conversation,
      }),
  };
  const progress = card.status === 'running' ? cardProgress(card.summary) : null;
  const more =
    actions && view === null && (body !== null || kind === 'artifact') ? (
      <WidgetCardActions artifact={artifact === undefined ? undefined : artifact} host={host}>
        {artifact?.pluginFolder !== undefined && <PluginFolderActions artifact={artifact} />}
      </WidgetCardActions>
    ) : null;

  // Something in progress (a render): its title, how far it is, and a bar (design D11).
  if (progress !== null) {
    return (
      <section
        className={`${TRANSCRIPT_CARD} px-3.5 py-3`}
        data-conversation-card={card.id}
        data-card-progress
      >
        <div className="flex items-center gap-2.5">
          <LoaderCircle
            aria-hidden
            className="size-4 shrink-0 animate-spin text-muted-foreground"
          />
          <p className="min-w-0 flex-1 truncate font-semibold text-foreground text-sm">
            {card.title}
          </p>
          <p className="type-meta shrink-0 text-muted-foreground tabular-nums">{card.summary}</p>
        </div>
        <div
          aria-label={`${card.title}: ${Math.round(progress * 100)} %`}
          aria-valuemax={100}
          aria-valuemin={0}
          aria-valuenow={Math.round(progress * 100)}
          className="mt-2.5 h-1 overflow-hidden rounded-full bg-control"
          role="progressbar"
        >
          <div className="h-full rounded-full bg-running" style={{ width: `${progress * 100}%` }} />
        </div>
      </section>
    );
  }

  // An artifact: its preview beside its name, and Open in panel (design D11).
  if (kind === 'artifact' && artifact !== undefined) {
    return (
      <section
        className={`${TRANSCRIPT_CARD} flex min-h-26 overflow-hidden`}
        data-conversation-card={card.id}
      >
        {render === null ? (
          <ArtifactPreview artifact={artifact} className="relative w-50 shrink-0 max-sm:w-28" />
        ) : (
          <WhenVisible className="relative w-50 shrink-0 overflow-hidden bg-black max-sm:w-28">
            <div className="size-full" data-card-render={render.id}>
              <CommonViewer compact file={render} playable />
            </div>
          </WhenVisible>
        )}
        <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5 px-3.5 py-3">
          <p className="w-full truncate font-semibold text-foreground text-sm">{card.title}</p>
          <p className="type-meta w-full truncate text-muted-foreground">
            {[artifact.pluginTitle || artifact.typeTitle, updatedLabel(artifact.modifiedAt)]
              .filter(Boolean)
              .join(' · ')}
          </p>
          <div className="mt-auto flex items-center gap-1 pt-2">
            {actions && onOpenArtifact !== undefined && (
              <Button
                className="h-8 rounded-control px-3.5"
                onClick={() => onOpenArtifact(artifact)}
                size="sm"
                type="button"
                variant="outline"
              >
                Open in panel
              </Button>
            )}
            {more !== null && <CardMoreActions>{more}</CardMoreActions>}
          </div>
        </div>
      </section>
    );
  }

  // A data widget: its source and title on one line, its figure, and its other actions in "More".
  if (isDataKind(kind) && body !== null) {
    return (
      <section
        className={`${TRANSCRIPT_CARD} relative px-0.5 py-0.5`}
        data-conversation-card={card.id}
      >
        <header className="type-meta flex items-center gap-2 px-3.5 pt-2.5 text-muted-foreground">
          {card.status !== 'info' && (
            <status.Icon
              aria-label={status.label || undefined}
              className={`size-3.5 shrink-0 ${status.tone} ${card.status === 'running' ? 'animate-spin' : ''}`}
            />
          )}
          <span className="min-w-0 flex-1 truncate">
            {[source, card.title].filter(Boolean).join(' · ')}
          </span>
          {more !== null && <CardMoreActions>{more}</CardMoreActions>}
        </header>
        {body}
        {card.summary !== null && (
          <p className="type-meta -mt-1 px-3.5 pb-2.5 text-muted-foreground">{card.summary}</p>
        )}
      </section>
    );
  }

  return (
    <Artifact className="my-3" data-conversation-card={card.id}>
      <ArtifactHeader>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {card.status !== 'info' && (
            <status.Icon
              aria-label={status.label || undefined}
              className={`size-4 shrink-0 ${status.tone} ${card.status === 'running' ? 'animate-spin' : ''}`}
            />
          )}
          <div className="min-w-0">
            <ArtifactTitle>{card.title}</ArtifactTitle>
            <ArtifactDescription>
              {[source, status.label, card.summary].filter(Boolean).join(' · ')}
            </ArtifactDescription>
          </div>
        </div>
      </ArtifactHeader>
      <ArtifactContent>
        {body ?? (
          <p className="px-4 py-3 text-muted-foreground text-xs">
            {kind === 'artifact'
              ? 'This artifact is no longer in this conversation.'
              : 'This widget is no longer available: its plugin was turned off or removed.'}
          </p>
        )}
        {actions && view === null && (body !== null || kind === 'artifact') && (
          <div className="border-t px-3 py-1.5 empty:hidden">
            <WidgetCardActions artifact={artifact === undefined ? undefined : artifact} host={host}>
              {artifact?.pluginFolder !== undefined && <PluginFolderActions artifact={artifact} />}
              {artifact !== undefined && onOpenArtifact !== undefined ? (
                <Button
                  onClick={() => onOpenArtifact(artifact)}
                  size="xs"
                  type="button"
                  variant="ghost"
                >
                  <ExternalLink />
                  Open
                </Button>
              ) : undefined}
            </WidgetCardActions>
          </div>
        )}
      </ArtifactContent>
    </Artifact>
  );
}
